import { bp } from './theme';

/**
 * Responsive helpers.
 *
 * Mobile-first. Write the base (phone) styles first, then layer larger screens
 * on top with `up()`:
 *
 *   const Grid = styled.div`
 *     display: grid;
 *     grid-template-columns: 1fr;              // phone
 *     gap: 16px;
 *     ${up('md')} { grid-template-columns: 1fr 1fr; }   // tablet+
 *     ${up('lg')} { grid-template-columns: repeat(3, 1fr); gap: 24px; }
 *   `;
 *
 * `down()` (max-width) is available for the genuinely desktop-first cases, of
 * which this app has many — the rail-to-tab-bar and table-to-card reflows read
 * more naturally written downward.
 *
 * THIS IS THE ONLY BREAKPOINT SOURCE IN THE APP. A parallel `media.*` map keyed
 * off a separate `sizes` object used to live here as "the legacy API"; it had
 * zero callers and its boundaries were max-width EQUAL to the breakpoint rather
 * than one hundredth below it, so the two systems both fired in a 1px band.
 * design_system.md:464 records exactly that class of drift as the defect this
 * file was created to end, so the second system is gone rather than preserved.
 * Everything responsive resolves through `bp` in theme.ts.
 */

export type Breakpoint = keyof typeof bp;

/** Mobile-first min-width query. Returns a raw media string for `${up('md')} { … }`. */
export const up = (key: Breakpoint): string =>
  `@media (min-width: ${bp[key]}px)`;

/** Desktop-first max-width query. `${down('md')} { … }` targets < breakpoint. */
export const down = (key: Breakpoint): string =>
  `@media (max-width: ${bp[key] - 0.02}px)`;

/** Range query between two breakpoints (inclusive lower, exclusive upper). */
export const between = (lower: Breakpoint, upper: Breakpoint): string =>
  `@media (min-width: ${bp[lower]}px) and (max-width: ${bp[upper] - 0.02}px)`;

/**
 * TOUCH INPUT, not narrow screen.
 *
 * The 44px minimum tap size (design_system.md rule 5) is about fingers, and a
 * width query gets that wrong in both directions: a half-width desktop window
 * is not a phone, and a 1280px iPad is. `hover: none and pointer: coarse` asks
 * the only question that matters.
 *
 * Use it to GROW a control on touch while leaving the desk-density design
 * alone — the dense 32px icon buttons in the IQ chat header and the gallery
 * tile actions are deliberate at a mouse, and only wrong under a thumb.
 */
export const touch = '@media (hover: none) and (pointer: coarse)';

/**
 * A PHONE HELD SIDEWAYS. The axis this app kept forgetting.
 *
 * Every other query here is about width, and a phone in landscape is WIDE — an
 * iPhone 14 Pro Max is 932x430 — so it matches none of the mobile rules and
 * takes the desktop layout in 430px of height. The wizard shells spend 64px top
 * and bottom of that on padding alone before any content exists.
 *
 * Height AND orientation, deliberately: height alone fires on a short desktop
 * window, where nothing is wrong and trimming padding would be a regression.
 *
 * DO NOT ADD A MAX-WIDTH BOUND, and do not "fix" the two queries that have one.
 * notification-dock and iq-dock both use `(max-width: md) and (max-height: 480)`
 * because what they are avoiding is the BOTTOM TAB BAR, and that bar only exists
 * below md. A 932px landscape phone takes the desk layout — left rail, no bottom
 * bar — so their width bound is correct and this helper's absence of one is
 * correct for a different reason: padding is wrong at that height either way.
 *
 * Use it to TRIM vertical rhythm, never to change layout structure — a landscape
 * phone wants the same screen, just not the same breathing room.
 */
export const shortLandscape =
  '@media (max-height: 480px) and (orientation: landscape)';
